/*
 * <nl-w2-quiz>: product finder "Najděte svůj produkt" (sections/nl-w2-quiz.liquid).
 *
 * Flow: 3 steps of radio cards (area, form, doses) -> results with 2 to 4 products.
 *   1. products of the mapped collection from <collection url>/products.json (published products only), available
 *      variants only; topped up from the fallback collection (then all products) when fewer than 2 are available
 *   2. ranking: mapped collection first, then preferred form, then preferred doses per day, then collection order
 *   3. cards from the Section Rendering API: <root>products/<handle>?section_id=nl-w2-quiz-card
 *      (THE product card + data-dose / data-form from metafields + explicit add to cart through <product-form>)
 * Prefetch starts as soon as an area is chosen, so the results are usually ready when the last step is answered.
 *
 * Motion: step transitions and card staggers with transform / opacity only, skipped under reduced motion.
 * Pointer choices advance automatically; keyboard users confirm with the Continue button (arrow keys never jump).
 * Emits `quiz:complete` { area, form, dose, handles } on document for analytics.
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-quiz')) return;

  const NL = (window.NL = window.NL || {});
  const doc = document;
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fold = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  const formFromText = (text) => {
    const t = fold(text);
    if (/prasek|prasku|prasky|powder/.test(t)) return 'prasek';
    if (/kaps|capsul/.test(t)) return 'kapsle';
    return '';
  };
  const rootUrl = () => {
    const root = (NL.routes && NL.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const pad = (n) => String(n).padStart(2, '0');
  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 800 }) : window.setTimeout(fn, 120));

  /* ---------------- data (shared across instances) ---------------- */

  const productsCache = new Map();
  const cardsCache = new Map();

  const fetchProducts = (url) => {
    if (!url) return Promise.resolve([]);
    if (!productsCache.has(url)) {
      const base = url.split('?')[0].replace(/\/$/, '');
      const promise = fetch(`${base}/products.json?limit=30`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) =>
          (data && Array.isArray(data.products) ? data.products : []).filter(
            (product) => product && product.handle && Array.isArray(product.variants) && product.variants.some((v) => v.available)
          )
        )
        .catch(() => {
          productsCache.delete(url);
          return [];
        });
      productsCache.set(url, promise);
    }
    return productsCache.get(url);
  };

  const fetchCard = (handle, section) => {
    if (!cardsCache.has(handle)) {
      const promise = fetch(`${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(section)}`, {
        credentials: 'same-origin',
      })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (!html) return null;
          const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quiz-card]');
          if (!card || card.dataset.available !== 'true') return null;
          return {
            html: card.outerHTML,
            dose: parseInt(card.dataset.dose, 10) || 0,
            form: card.dataset.form || '',
          };
        })
        .catch(() => {
          cardsCache.delete(handle);
          return null;
        });
      cardsCache.set(handle, promise);
    }
    return cardsCache.get(handle);
  };

  const tagsText = (product) =>
    [product.product_type, Array.isArray(product.tags) ? product.tags.join(' ') : product.tags, product.title].join(' ');

  /* ---------------- element ---------------- */

  class W2Quiz extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.form = this.querySelector('[data-quiz-form]');
      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      if (!this.form || !this.steps.length) return;
      this._bound = true;

      const script = this.querySelector('script[data-quiz-config]');
      try {
        this.config = JSON.parse(script ? script.textContent : '{}');
      } catch (error) {
        this.config = {};
      }
      this.config.min = this.config.min || 2;
      this.config.max = Math.max(this.config.min, Math.min(4, this.config.max || 4));
      this.strings = this.config.strings || {};

      this.results = this.querySelector('[data-quiz-results]');
      this.grid = this.querySelector('[data-quiz-grid]');
      this.summary = this.querySelector('[data-quiz-summary]');
      this.note = this.querySelector('[data-quiz-note]');
      this.error = this.querySelector('[data-quiz-error]');
      this.collectionLink = this.querySelector('[data-quiz-collection]');
      this.nav = this.querySelector('[data-quiz-nav]');
      this.back = this.querySelector('[data-quiz-back]');
      this.next = this.querySelector('[data-quiz-next]');
      this.nextLabel = this.querySelector('[data-quiz-next-label]');
      this.count = this.querySelector('[data-quiz-count]');
      this.stepper = Array.from(this.querySelectorAll('[data-quiz-stepper]'));
      this.live = this.querySelector('[data-quiz-live]');
      this.restartButton = this.querySelector('[data-quiz-restart]');

      this.index = 0;
      this.busy = false;
      this.viaPointer = false;
      this.token = 0;

      this.form.addEventListener('pointerdown', (event) => {
        const option = event.target.closest('.nl-w2-option');
        const input = option && option.querySelector('input');
        this.viaPointer = Boolean(option);
        this.wasChecked = Boolean(input && input.checked);
      });
      // A second click on the already chosen card (for example after going back) continues as well
      this.form.addEventListener('click', (event) => {
        const option = event.target.closest('.nl-w2-option');
        if (!option || !this.viaPointer || !this.wasChecked) return;
        this.viaPointer = false;
        this.wasChecked = false;
        window.clearTimeout(this.autoTimer);
        this.autoTimer = window.setTimeout(() => this.advance(), reduced() ? 0 : 240);
      });
      this.form.addEventListener('keydown', () => {
        this.viaPointer = false;
      });
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.advance();
      });
      if (this.back) this.back.addEventListener('click', () => this.goTo(this.index - 1, -1));
      if (this.restartButton) this.restartButton.addEventListener('click', () => this.restart());

      this.onBlockSelect = (event) => {
        if (this.contains(event.target) && this.index !== 0) this.jump(0);
      };
      doc.addEventListener('shopify:block:select', this.onBlockSelect);

      this.classList.add('is-ready');
      this.update(false);
      this.fromUrl();
    }

    disconnectedCallback() {
      if (this.onBlockSelect) doc.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.clearTimeout(this.autoTimer);
    }

    /* ---------- state ---------- */

    checked(name) {
      return this.form.querySelector(`input[name="${name}"]:checked`);
    }

    answers() {
      const area = this.checked('area');
      const form = this.checked('form');
      const dose = this.checked('dose');
      return {
        area: area ? { value: area.value, url: area.dataset.url, label: area.dataset.label } : null,
        form: form ? { value: form.value, label: form.dataset.label } : { value: 'bez', label: '' },
        dose: dose ? { value: dose.value, label: dose.dataset.label } : { value: 'bez', label: '' },
      };
    }

    currentHasAnswer() {
      const step = this.steps[this.index];
      return Boolean(step && step.querySelector('input[type="radio"]:checked'));
    }

    syncNext() {
      if (!this.next) return;
      this.next.disabled = !this.currentHasAnswer();
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches || !input.matches('input[type="radio"]')) return;
      this.syncNext();
      if (input.name === 'area') this.prefetch();
      if (this.viaPointer && !this.busy) {
        this.viaPointer = false;
        window.clearTimeout(this.autoTimer);
        this.autoTimer = window.setTimeout(() => this.advance(), reduced() ? 0 : 420);
      }
    }

    advance() {
      window.clearTimeout(this.autoTimer);
      if (this.busy || !this.currentHasAnswer()) return;
      if (this.index >= this.steps.length - 1) this.showResults();
      else this.goTo(this.index + 1, 1);
    }

    /* ---------- transitions ---------- */

    panelAt(index) {
      return index >= this.steps.length ? this.results : this.steps[index];
    }

    swap(from, to, direction, done) {
      window.clearTimeout(this.autoTimer);
      const enter = () => {
        if (from && from !== to) {
          from.hidden = true;
          from.classList.remove('is-leaving', 'is-leaving-back');
        }
        to.hidden = false;
        if (!reduced()) {
          const cls = direction < 0 ? 'is-entering-back' : 'is-entering';
          to.classList.add(cls);
          void to.offsetWidth;
          to.classList.remove(cls);
        }
        this.busy = false;
        done();
      };
      if (!from || from === to || reduced()) {
        enter();
        return;
      }
      this.busy = true;
      from.classList.add(direction < 0 ? 'is-leaving-back' : 'is-leaving');
      window.setTimeout(enter, 220);
    }

    goTo(target, direction) {
      if (this.busy || target < 0 || target >= this.steps.length) return;
      this.swap(this.panelAt(this.index), this.steps[target], direction, () => {
        this.index = target;
        this.update(true);
      });
    }

    jump(target) {
      [...this.steps, this.results].forEach((panel, i) => {
        if (panel) panel.hidden = i !== target;
      });
      this.index = target;
      this.update(false);
    }

    update(focus) {
      const total = this.steps.length;
      const atResults = this.index >= total;
      const n = Math.min(this.index + 1, total);
      if (this.count) this.count.textContent = pad(n);
      this.style.setProperty('--quiz-p', String(atResults ? 1 : n / total));
      this.classList.toggle('is-results', atResults);
      this.stepper.forEach((item, i) => {
        item.classList.toggle('is-current', !atResults && i === this.index);
        item.classList.toggle('is-done', atResults || i < this.index);
      });
      if (this.nav) this.nav.hidden = atResults;
      if (this.back) this.back.hidden = this.index === 0 || atResults;
      if (this.nextLabel) {
        this.nextLabel.textContent =
          this.index === total - 1 ? this.strings.submit || this.nextLabel.textContent : this.strings.next || this.nextLabel.textContent;
      }
      this.syncNext();

      if (!focus) return;
      const heading = atResults
        ? this.results.querySelector('[data-quiz-results-heading]')
        : this.steps[this.index].querySelector('[data-quiz-heading]');
      if (heading) heading.focus({ preventScroll: true });
      this.ensureVisible();
      if (!atResults && this.live && heading) {
        const label = String(this.strings.stepOf || '')
          .replace('%n%', String(n))
          .replace('%total%', String(total));
        this.live.textContent = label ? `${label}. ${heading.textContent.trim()}` : heading.textContent.trim();
      }
    }

    ensureVisible() {
      // fix-home-1: scrolling up reveals the sticky header again, so keep the full header height free (not only the
      // currently visible part), otherwise the 02 / 03 counter ends up under the header on phones
      const css = getComputedStyle(doc.documentElement);
      const offset = Math.max(
        parseFloat(css.getPropertyValue('--nl-header-offset')) || 0,
        parseFloat(css.getPropertyValue('--nl-header-height')) || 0
      );
      const top = this.getBoundingClientRect().top;
      if (top >= offset + 8) return;
      window.scrollTo({ top: window.scrollY + top - offset - 16, behavior: reduced() ? 'auto' : 'smooth' });
    }

    /* ---------- data ---------- */

    prefetch() {
      const a = this.answers();
      if (!a.area) return;
      idle(() => {
        fetchProducts(a.area.url).then((list) => {
          list.slice(0, 6).forEach((product) => fetchCard(product.handle, this.config.cardSection || 'nl-w2-quiz-card'));
        });
      });
    }

    async recommend(a) {
      const { min, max } = this.config;
      const section = this.config.cardSection || 'nl-w2-quiz-card';
      const primary = await fetchProducts(a.area.url);
      const pool = primary.map((product) => ({ product, primary: true }));
      const seen = new Set(primary.map((product) => product.handle));
      const add = (list) =>
        list.forEach((product) => {
          if (seen.has(product.handle)) return;
          seen.add(product.handle);
          pool.push({ product, primary: false });
        });

      if (pool.length < min && this.config.fallbackUrl && this.config.fallbackUrl !== a.area.url) {
        add(await fetchProducts(this.config.fallbackUrl));
      }
      if (pool.length < min && this.config.allUrl && this.config.allUrl !== this.config.fallbackUrl) {
        add(await fetchProducts(this.config.allUrl));
      }
      const topup = pool.length > primary.length;

      const wantForm = a.form.value;
      const wantDose = a.dose.value;
      pool.forEach((entry, i) => {
        entry.order = i;
        entry.textForm = formFromText(tagsText(entry.product));
      });

      const preScore = (entry) => (entry.primary ? 100 : 0) + (wantForm !== 'bez' && entry.textForm === wantForm ? 10 : 0);
      const candidates = pool
        .slice()
        .sort((x, y) => preScore(y) - preScore(x) || x.order - y.order)
        .slice(0, Math.max(max + 2, 6));

      const cards = await Promise.all(candidates.map((entry) => fetchCard(entry.product.handle, section)));
      const scored = candidates
        .map((entry, i) => Object.assign(entry, { card: cards[i] }))
        .filter((entry) => entry.card);

      // fix-home-1 (QA v3): answers are filters, not hints. Products that match the chosen form and doses come first and
      // alone fill the results; only when fewer than `min` match, the list is topped up (same area first) and every
      // extra card carries a visible label (other dose, other form, other area).
      const formOf = (entry) => entry.card.form || entry.textForm;
      const formOk = (entry) => wantForm === 'bez' || formOf(entry) === wantForm;
      const doseOk = (entry) =>
        wantDose === 'bez' || (wantDose === '1' ? entry.card.dose === 1 : entry.card.dose >= 2);
      const tier = (entry) => {
        const match = formOk(entry) && doseOk(entry);
        if (entry.primary) return match ? 0 : 1;
        return match ? 2 : 3;
      };
      scored.sort((x, y) => tier(x) - tier(y) || x.order - y.order);

      const exact = scored.filter((entry) => tier(entry) === 0);
      const items = exact.slice(0, max);
      if (items.length < min) {
        scored.forEach((entry) => {
          if (items.length >= min || items.includes(entry)) return;
          items.push(entry);
        });
      }
      items.forEach((entry) => {
        entry.alt = '';
        if (!entry.primary) entry.alt = 'area';
        else if (!doseOk(entry)) entry.alt = 'dose';
        else if (!formOk(entry)) entry.alt = 'form';
      });

      const formMissing = wantForm !== 'bez' && items.length > 0 && !items.some(formOk);
      return {
        items,
        topup: topup && items.some((entry) => !entry.primary),
        doseAlt: items.some((entry) => entry.alt === 'dose'),
        primaryCount: primary.length,
        formMissing,
        wantForm,
        wantDose,
      };
    }

    /* ---------- results ---------- */

    renderSummary(a) {
      if (!this.summary) return;
      const labels = [a.area && a.area.label, a.form.value !== 'bez' && a.form.label, a.dose.value !== 'bez' && a.dose.label].filter(Boolean);
      this.summary.replaceChildren(
        ...labels.map((label) => {
          const li = doc.createElement('li');
          li.className = 'chip chip--outline';
          li.textContent = label;
          return li;
        })
      );
    }

    renderSkeletons() {
      const items = [];
      for (let i = 0; i < this.config.max; i += 1) {
        const li = doc.createElement('li');
        li.className = 'nl-w2-quiz__item is-skeleton';
        li.setAttribute('aria-hidden', 'true');
        li.innerHTML =
          '<div class="nl-skeleton nl-skeleton--card"></div><div class="nl-skeleton nl-skeleton--title"></div><div class="nl-skeleton nl-skeleton--text"></div>';
        items.push(li);
      }
      this.grid.style.setProperty('--cols', String(this.config.max));
      this.grid.dataset.count = String(this.config.max);
      this.grid.classList.remove('is-in');
      this.grid.setAttribute('aria-busy', 'true');
      this.grid.replaceChildren(...items);
    }

    async showResults() {
      const a = this.answers();
      if (!a.area || this.busy) return;
      const token = ++this.token;

      this.renderSummary(a);
      this.renderSkeletons();
      if (this.note) this.note.hidden = true;
      if (this.error) this.error.hidden = true;
      if (this.collectionLink && a.area.url) this.collectionLink.href = a.area.url;
      this.writeUrl(a);

      const started = performance.now();
      this.swap(this.panelAt(this.index), this.results, 1, () => {
        this.index = this.steps.length;
        this.update(true);
      });

      let picks;
      try {
        picks = await this.recommend(a);
      } catch (error) {
        picks = { items: [] };
      }
      // Let the panel transition settle before the cards come in (calm, no flicker)
      const elapsed = performance.now() - started;
      if (!reduced() && elapsed < 420) await sleep(420 - elapsed);
      if (token !== this.token) return;
      this.renderPicks(picks, a);
    }

    renderPicks(picks, a) {
      this.grid.setAttribute('aria-busy', 'false');
      if (!picks.items || !picks.items.length) {
        this.grid.replaceChildren();
        if (this.error) this.error.hidden = false;
        return;
      }

      // An empty area collection is a dead end: send "show the whole category" to the fallback collection instead
      if (this.collectionLink && picks.primaryCount === 0 && this.config.fallbackUrl) {
        this.collectionLink.href = this.config.fallbackUrl;
      }

      const notes = [];
      if (picks.doseAlt && this.strings.doseNote) notes.push(this.strings.doseNote);
      if (picks.topup && this.strings.topupNote) notes.push(this.strings.topupNote);
      if (picks.formMissing && this.strings.formNote) notes.push(this.strings.formNote);
      if (this.note) {
        this.note.textContent = notes.join(' ');
        this.note.hidden = notes.length === 0;
      }

      const nodes = picks.items.map((entry, i) => {
        const li = doc.createElement('li');
        li.className = 'nl-w2-quiz__item';
        li.style.setProperty('--i', String(i));
        const template = doc.createElement('template');
        template.innerHTML = entry.card.html;
        li.append(template.content);
        const altLabel = { dose: this.strings.altDose, form: this.strings.altForm, area: this.strings.altArea }[entry.alt];
        if (altLabel) {
          li.classList.add('is-alt');
          const badge = doc.createElement('p');
          badge.className = 'nl-w2-quiz__alt';
          badge.textContent = altLabel;
          li.prepend(badge);
        }
        const form = entry.card.form || entry.textForm;
        const formFact = li.querySelector('[data-fact="form"]');
        if (formFact && picks.wantForm !== 'bez' && form === picks.wantForm) formFact.classList.add('is-match');
        const doseFact = li.querySelector('[data-fact="dose"]');
        const doseMatch =
          (picks.wantDose === '1' && entry.card.dose === 1) || (picks.wantDose === '2' && entry.card.dose >= 2);
        if (doseFact && doseMatch) doseFact.classList.add('is-match');
        if (doseFact && entry.alt === 'dose') doseFact.classList.add('is-mismatch');
        return li;
      });

      this.grid.style.setProperty('--cols', String(nodes.length));
      this.grid.dataset.count = String(nodes.length);
      this.grid.classList.remove('is-in');
      this.grid.replaceChildren(...nodes);
      if (!reduced()) {
        void this.grid.offsetWidth;
        this.grid.classList.add('is-in');
      }

      const handles = picks.items.map((entry) => entry.product.handle);
      doc.dispatchEvent(
        new CustomEvent('quiz:complete', {
          bubbles: true,
          detail: { area: a.area.value, form: a.form.value, dose: a.dose.value, handles },
        })
      );
    }

    restart() {
      this.token += 1;
      this.form.reset();
      this.writeUrl(null);
      this.swap(this.results, this.steps[0], -1, () => {
        this.index = 0;
        this.update(true);
      });
    }

    /* ---------- deep links ---------- */

    writeUrl(a) {
      try {
        const url = new URL(window.location.href);
        ['oblast', 'forma', 'davky'].forEach((key) => url.searchParams.delete(key));
        if (a && a.area) {
          url.searchParams.set('oblast', a.area.value);
          if (this.form.querySelector('input[name="form"]')) url.searchParams.set('forma', a.form.value);
          url.searchParams.set('davky', a.dose.value);
        }
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* history blocked: ignore */
      }
    }

    fromUrl() {
      const params = new URLSearchParams(window.location.search);
      const pick = (name, value) => {
        if (!value) return false;
        const input = Array.from(this.form.querySelectorAll(`input[name="${name}"]`)).find((el) => el.value === value);
        if (input) input.checked = true;
        return Boolean(input);
      };
      if (!pick('area', params.get('oblast'))) return;
      this.prefetch();
      // The form question exists only when the catalogue has two or more forms (sections/nl-w2-quiz.liquid)
      const formStep = this.steps.findIndex((step) => step.querySelector('input[name="form"]'));
      const hasForm = formStep < 0 || pick('form', params.get('forma'));
      const hasDose = hasForm && pick('dose', params.get('davky'));
      if (hasDose) {
        this.jump(this.steps.length - 1);
        this.showResults();
      } else {
        this.jump(hasForm ? this.steps.length - 1 : formStep);
      }
    }
  }

  customElements.define('nl-w2-quiz', W2Quiz);
})();
